import {
    artistNameInvalidCheck,
    copyrightOfficeNumberInvalidCheck,
    durationInvalidCheck,
    isrcInvalidCheck,
    iswcInvalidCheck,
    labelInvalidCheck,
    languageInvalidCheck,
    recordingTitleInvalidCheck,
    rightsholderProprietaryIdInvalidCheck,
    workTitleInvalidCheck,
} from '@App/helpers/work-entities-fields-validators'
import {
    akaTitleInvalidCheck,
    akaTitleTypeInvalidCheck,
    publisherNameInvalidCheck,
    publisherShareInvalidCheck,
    writerRoleInvalidCheck,
    writerFirstNameInvalidCheck,
    writerLastNameInvalidCheck,
    isComposerPresent,
} from '@App/helpers/work-entities/fields-validators'
import { IPIValidator } from '@App/helpers/validate'
import { OptionModel } from '@Entities/lib'
import { WorkModel } from '@Entities/work/work'
import { AlternativeTitleModel } from '@Entities/work/alternative-title-model'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { WriterModel } from '@Entities/work/writer-model'
import { RecordingModel } from '@Entities/work/recording-model'

// These validators check fields in the respective blocks and return isError: boolean
export const akaTitlesInvalidCheck = (
    akaTitles: AlternativeTitleModel[],
    akaLib: OptionModel[],
    langsLib: OptionModel[]
) => {
    if (!akaTitles.length) {
        return false
    }

    const isError: boolean = akaTitles.some((akaTitle: AlternativeTitleModel) => {
        const title: string | null = akaTitle.title // required, max-length: 500
        const titleType: number | null = akaTitle.titleType // required, match with id from lib
        const languageCode: number | null = akaTitle.languageCode // match with id from lib

        return (
            akaTitleInvalidCheck(title) ||
            akaTitleTypeInvalidCheck(titleType, akaLib) ||
            languageInvalidCheck(languageCode, langsLib, false)
        )
    })

    return isError
}

export const workDetailsInvalidCheck = (work: WorkModel, akaLib: OptionModel[], langsLib: OptionModel[]) => {
    const validations: any = {
        titleError: false,
        durationError: false,
        copyrightOfficeNumberError: false,
        copyrightOfficeDateError: false,
        rightsholderProprietaryIdError: false,
        iswcError: false,
        // languageCodeError: true,
        akaTitlesError: false,
    }

    validations.titleError = workTitleInvalidCheck(work.title)
    validations.durationError = durationInvalidCheck(work.duration ?? null)
    validations.copyrightOfficeNumberError = copyrightOfficeNumberInvalidCheck(work.copyrightOfficeNumber ?? null)
    validations.rightsholderProprietaryIdError = rightsholderProprietaryIdInvalidCheck(
        work.rightsholderProprietaryId ?? null
    )
    validations.iswcError = iswcInvalidCheck(work.iswc ?? null)
    // validations.languageCodeError = languageInvalidCheck(work.languageCode, langsLib, true)
    validations.akaTitlesError = work.alternativeTitles.length
        ? akaTitlesInvalidCheck(work.alternativeTitles, akaLib, langsLib)
        : false

    let invalidCounter = 0

    for (const validationStatus in validations) {
        if (validations[validationStatus]) {
            invalidCounter += 1
        }
    }

    return !!invalidCounter
}

export const writersInvalidCheck = (writers: WriterModel[], rolesLib: OptionModel[], isExistingWork = true) => {
    if (!writers.length && isExistingWork) {
        return true
    } else if (!writers.length) {
        return false
    }

    const isError: boolean = writers.some((writer: WriterModel) => {
        const firstName: string | null = writer.firstName // max-length: 60
        const lastName: string = writer.lastName // required, max-length: 200
        const ipiNumber: string | null = writer.ipiNumber // max-length: 11, ipi validator, numeric
        const roleCode: number | null = writer.roleCode // required, match with id from lib

        return (
            writerFirstNameInvalidCheck(firstName) ||
            writerLastNameInvalidCheck(lastName) ||
            !IPIValidator(ipiNumber, false) ||
            writerRoleInvalidCheck(roleCode, rolesLib)
        )
    })
    return isError || !isComposerPresent(writers)
}

export const publishersInvalidCheck = (originalPublishers: OriginalPublisherModel[], skipIpiCheck = false) => {
    if (!originalPublishers.length) {
        return true
    }

    const isError: boolean = originalPublishers.some((op: OriginalPublisherModel) => {
        const publisherNameOP: string = op.publisherName
        const publisherShareOP: number | null = op.publisherShare
        const ipiNumberOP: string | null = op.ipiNumber
        const administratorPublishers: PublisherModel[] = op.administratorPublishers

        let checkOpShares = true

        const administratorPublishersError: boolean = administratorPublishers.some((ap: PublisherModel) => {
            if (!administratorPublishers.length) {
                return false
            }

            checkOpShares = false

            const publisherNameAP: string = ap.publisherName
            const publisherShareAP: number | null = ap.publisherShare
            const ipiNumberAP: string | null = ap.ipiNumber

            return (
                publisherNameInvalidCheck(publisherNameAP) ||
                publisherShareInvalidCheck(publisherShareAP) ||
                !IPIValidator(ipiNumberAP, !skipIpiCheck)
            )
        })

        return (
            publisherNameInvalidCheck(publisherNameOP) ||
            (checkOpShares && publisherShareInvalidCheck(publisherShareOP)) ||
            !IPIValidator(ipiNumberOP, !skipIpiCheck) ||
            administratorPublishersError
        )
    })

    return isError
}

export const recordingsInvalidCheck = (recordings: RecordingModel[], skipAmendCheck = false) => {
    if (!recordings.length) {
        return false
    }

    const isError: boolean = recordings.some((recording: RecordingModel) => {
        if (skipAmendCheck || !('amend' in recording) || recording.amend === true) {
            const title: string = recording.title // required, max-length: 200
            const artistName: string = recording.artistName // required, max-length: 60
            const isrc: string | null = recording.isrc // max-length: 12
            const duration: string | null = recording.duration // 00:00 format
            const label: string | null = recording.label // max-length: 60

            return (
                recordingTitleInvalidCheck(title) ||
                artistNameInvalidCheck(artistName) ||
                isrcInvalidCheck(isrc) ||
                durationInvalidCheck(duration) ||
                labelInvalidCheck(label)
            )
        } else {
            return false
        }
    })

    return isError
}

// this validator checking data and errors on form and return isError: boolean
export const formFieldsInvalidCheck = (data: any, validations: any): boolean => {
    let errorCount = 0

    for (const field in validations) {
        if (validations[field].error || (validations[field].required && field !== 'publisherShare' && !data[field])) {
            errorCount += 1
        }
    }

    return !!errorCount
}
